import { useEffect } from "react";

export type AreaRole = "owner" | "member" | "admin";

/**
 * Stamps the current area on <html> so the stylesheet can repaint the app for it.
 *
 * The owner and member areas are one tap apart and, by design, the same shapes in the same order.
 * That is right for someone who runs a business and has a participant in it, and wrong for anyone
 * trying to work out which of two open tabs is which. `[data-role="owner"]` in `styles.css` swaps
 * the accent and the page ground; `[data-role="admin"]` swaps them again to a neutral dark so
 * Control Center reads as a third place. This hook is the few lines that turn them on.
 *
 * On <html> rather than a wrapper, because the body's own background is painted from these tokens
 * and a wrapper cannot reach it. The attribute is removed on unmount rather than set to "member",
 * so the member side is the plain stylesheet and never a second theme to keep in step.
 */
export function useRoleTheme(role: AreaRole): void {
  useEffect(() => {
    const root = document.documentElement;
    if (role === "member") delete root.dataset.role;
    else root.dataset.role = role;

    return () => {
      delete root.dataset.role;
    };
  }, [role]);
}
